import { api } from '@/shared/api';
import { unwrap, unwrapCollection } from '@/shared/api/envelope';

import {
    toMessagePreview,
    toMessageTemplate,
    toTestMailResult,
    type MessageChannel,
    type MessageDraft,
    type MessagePreview,
    type MessageTemplate,
    type TestMailResult,
} from './model';
import {
    notificationTemplateEntrySchema,
    notificationTemplatePreviewResourceSchema,
    notificationTemplateResourceSchema,
    notificationTestMailResourceSchema,
} from './types';

/** Schrijfacties en voorbeelden gaan nooit in de offline cache. */
const WRITE = { cacheKey: false } as const;

export interface MessageDraftInput extends MessageDraft {
    key: string;
    channel: MessageChannel;
}

/**
 * De berichten aan klanten (`02-api-contract.md` §Notificaties). Lezen mag
 * iedereen in het bedrijf; schrijven, een voorbeeld en een testmail vragen
 * `settings.manage`.
 */
export const notificationTemplateApi = {
    /** Elk bericht aan klanten op elk kanaal, met de tekst die nu uitgaat. */
    async clientMessages(): Promise<MessageTemplate[]> {
        const page = unwrapCollection(
            notificationTemplateEntrySchema,
            await api.get('/notification-templates', { query: { audience: 'customer' } }),
        );

        return page.items
            .map(toMessageTemplate)
            .filter((template): template is MessageTemplate => template !== null);
    },

    /** Een eigen tekst voor één bericht en kanaal; vervangt wat er stond. */
    async save(input: MessageDraftInput): Promise<void> {
        unwrap(
            notificationTemplateResourceSchema,
            await api.post(
                '/notification-templates',
                {
                    key: input.key,
                    channel: input.channel,
                    body: input.body,
                    ...(input.channel === 'mail' ? { subject: input.subject ?? '' } : {}),
                },
                WRITE,
            ),
        );
    },

    /** De eigen tekst weghalen: de standaardtekst gaat weer uit. */
    async restoreDefault(uuid: string): Promise<void> {
        await api.delete(`/notification-templates/${uuid}`, { ...WRITE, expect: 'none' });
    },

    /** De tekst met voorbeeldgegevens, een e-mail in de huisstijl van het bedrijf. */
    async preview(input: MessageDraftInput, signal?: AbortSignal): Promise<MessagePreview> {
        return toMessagePreview(
            unwrap(
                notificationTemplatePreviewResourceSchema,
                await api.post(
                    '/notification-templates/preview',
                    {
                        key: input.key,
                        channel: input.channel,
                        body: input.body,
                        ...(input.channel === 'mail' ? { subject: input.subject ?? '' } : {}),
                    },
                    { ...WRITE, ...(signal ? { signal } : {}) },
                ),
            ),
        );
    },

    /** De e-mail met voorbeeldgegevens naar je eigen adres. */
    async sendTestMail(input: {
        key: string;
        subject: string;
        body: string;
    }): Promise<TestMailResult> {
        return toTestMailResult(
            unwrap(
                notificationTestMailResourceSchema,
                await api.post('/notification-templates/test-mail', input, {
                    ...WRITE,
                    timeoutMs: 60_000,
                }),
            ),
        );
    },
};
